import { IconButton, type IconComponent, LuIcon } from "@overmap-ai/blocks"
import { Minus, Plus } from "lucide-react"
import { type ComponentPropsWithRef, memo, type MouseEventHandler, useCallback } from "react"

import { useFallbackEditor, useTextStyleState } from "../../hooks"
import type { OptionalEditorProps } from "../typings"

interface FontSizeButtonProps extends Omit<ComponentPropsWithRef<typeof IconButton>, "children">, OptionalEditorProps {
	direction: "increase" | "decrease"
}

export const FontSizeButton = memo<FontSizeButtonProps>((props) => {
	const { ref, onClick, editor: editorFromProps, direction, ...rest } = props

	const editor = useFallbackEditor(editorFromProps)
	const { fontSize = "16px" } = useTextStyleState(editor)

	const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			onClick?.(e)
			if (e.defaultPrevented || !fontSize) return
			const amount = direction === "increase" ? 1 : -1
			editor
				?.chain()
				.focus()
				.setFontSize(`${parseInt(fontSize) + amount}px`)
				.run()
		},
		[direction, editor, onClick, fontSize],
	)

	const getIconType = (): IconComponent => {
		switch (direction) {
			case "increase":
				return Plus
			case "decrease":
				return Minus
		}
	}

	return (
		<IconButton ref={ref} onClick={handleClick} {...rest}>
			<LuIcon icon={getIconType()} />
		</IconButton>
	)
})
FontSizeButton.displayName = "FontSizeButton"
